import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdLock } from 'react-icons/md'
import { changeUserPassword } from '@/lib/api/users/users-api'
import {
    getMyLoginHistory,
    LoginHistoryItem,
} from '@/lib/api/events/events-api'
import { logout } from '@/store/slices/authSlice'
import { useAppDispatch, useAuth } from '@/store/hooks'

function describeUserAgent(ua?: string | null): string {
    if (!ua) return 'Dispositivo desconhecido'
    const browser = /Edg/i.test(ua)
        ? 'Edge'
        : /OPR|Opera/i.test(ua)
          ? 'Opera'
          : /Chrome/i.test(ua)
            ? 'Chrome'
            : /Firefox/i.test(ua)
              ? 'Firefox'
              : /Safari/i.test(ua)
                ? 'Safari'
                : 'Navegador'
    const os = /Windows/i.test(ua)
        ? 'Windows'
        : /Android/i.test(ua)
          ? 'Android'
          : /iPhone|iPad|iOS/i.test(ua)
            ? 'iOS'
            : /Mac OS X|Macintosh/i.test(ua)
              ? 'macOS'
              : /Linux/i.test(ua)
                ? 'Linux'
                : 'Sistema'
    return `${browser} • ${os}`
}

function loginDayLabel(iso: string): string {
    const d = new Date(iso)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    const day = new Date(d)
    day.setHours(0, 0, 0, 0)
    if (day.getTime() === today.getTime()) return 'Hoje'
    if (day.getTime() === yesterday.getTime()) return 'Ontem'
    return d.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    })
}

function groupLoginsByDay(
    items: LoginHistoryItem[]
): { label: string; items: LoginHistoryItem[] }[] {
    const groups: { label: string; items: LoginHistoryItem[] }[] = []
    for (const item of items) {
        const label = loginDayLabel(item.createdAt)
        let group = groups.find(g => g.label === label)
        if (!group) {
            group = { label, items: [] }
            groups.push(group)
        }
        group.items.push(item)
    }
    return groups
}

/**
 * Aba de senha e segurança: troca de senha, encerramento de sessões e os
 * últimos acessos.
 *
 * Autocontida — todo o estado que usa é dela. O efeito do histórico não checa
 * mais `activeTab`: o componente só existe enquanto a aba está aberta, então
 * montar já é o sinal de que é hora de carregar.
 */
export default function PasswordTab() {
    const { user } = useAuth()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const [changingPassword, setChangingPassword] = useState(false)
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    })
    const [terminateSessions, setTerminateSessions] = useState(false)
    const [loginHistory, setLoginHistory] = useState<LoginHistoryItem[]>([])
    const [loadingLogins, setLoadingLogins] = useState(false)

    useEffect(() => {
        let cancelled = false
        setLoadingLogins(true)
        getMyLoginHistory()
            .then(res => {
                if (!cancelled && res.success && res.result?.items) {
                    setLoginHistory(res.result.items)
                }
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setLoadingLogins(false)
            })
        return () => {
            cancelled = true
        }
    }, [])

    const handleChangePassword = async () => {
        if (!user?.id) return

        if (!passwordData.currentPassword.trim()) {
            toast.warning('Preencha a senha atual')
            return
        }

        if (!passwordData.newPassword.trim()) {
            toast.warning('Preencha a nova senha')
            return
        }

        if (passwordData.newPassword.length < 6) {
            toast.warning('A nova senha deve ter no mínimo 6 caracteres')
            return
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            toast.warning('As senhas não coincidem')
            return
        }

        setChangingPassword(true)
        try {
            const response = await changeUserPassword(user.id, {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword,
                terminateSessions,
            })

            if (response.success) {
                setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                })
                if (terminateSessions) {
                    toast.success(
                        'Senha alterada. Todas as sessões foram encerradas — faça login novamente.'
                    )
                    dispatch(logout())
                    navigate('/login')
                } else {
                    toast.success('Senha alterada com sucesso')
                }
            } else {
                toast.error(response.error || 'Erro ao alterar senha')
            }
        } catch (error) {
            toast.error('Erro ao alterar senha')
        } finally {
            setChangingPassword(false)
        }
    }

    return (
            <div className="flex flex-col gap-6">
                <h2 className="text-xl font-bold text-base-content">
                    Alterar Senha
                </h2>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Senha Atual{' '}
                            <span className="text-error">
                                *
                            </span>
                        </span>
                    </label>
                    <div className="relative">
                        <MdLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                        <input
                            type="password"
                            placeholder="Digite sua senha atual"
                            className="input input-bordered w-full pl-10"
                            value={passwordData.currentPassword}
                            onChange={e =>
                                setPasswordData({
                                    ...passwordData,
                                    currentPassword:
                                        e.target.value,
                                })
                            }
                        />
                    </div>
                </div>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Nova Senha{' '}
                            <span className="text-error">
                                *
                            </span>
                        </span>
                    </label>
                    <div className="relative">
                        <MdLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                        <input
                            type="password"
                            placeholder="Digite sua nova senha"
                            className="input input-bordered w-full pl-10"
                            value={passwordData.newPassword}
                            onChange={e =>
                                setPasswordData({
                                    ...passwordData,
                                    newPassword: e.target.value,
                                })
                            }
                        />
                    </div>
                </div>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Confirmar Nova Senha{' '}
                            <span className="text-error">
                                *
                            </span>
                        </span>
                    </label>
                    <div className="relative">
                        <MdLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                        <input
                            type="password"
                            placeholder="Confirme sua nova senha"
                            className="input input-bordered w-full pl-10"
                            value={passwordData.confirmPassword}
                            onChange={e =>
                                setPasswordData({
                                    ...passwordData,
                                    confirmPassword:
                                        e.target.value,
                                })
                            }
                        />
                    </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                    <input
                        type="checkbox"
                        className="checkbox checkbox-secondary checkbox-sm mt-0.5"
                        checked={terminateSessions}
                        onChange={e =>
                            setTerminateSessions(
                                e.target.checked
                            )
                        }
                    />
                    <span className="text-sm text-base-content/70">
                        Encerrar todas as sessões ativas após a
                        troca. Você será desconectado de todos
                        os dispositivos (inclusive este) e
                        precisará entrar novamente.
                    </span>
                </label>

                <button
                    className="btn btn-secondary w-full"
                    onClick={handleChangePassword}
                    disabled={changingPassword}
                >
                    {changingPassword ? (
                        <span className="loading loading-spinner"></span>
                    ) : (
                        'Alterar Senha'
                    )}
                </button>

                <div className="mt-8 pt-6 border-t border-base-300/50">
                    <h3 className="text-lg font-semibold text-base-content mb-1">
                        Últimos acessos
                    </h3>
                    <p className="text-sm text-base-content/60 mb-4">
                        Logins recentes na sua conta. Se não
                        reconhecer algum acesso, troque a senha
                        e encerre as sessões.
                    </p>
                    {loadingLogins ? (
                        <div className="space-y-2">
                            {Array.from({ length: 3 }).map(
                                (_, i) => (
                                    <div
                                        key={i}
                                        className="h-12 rounded-lg bg-base-200/50 animate-pulse"
                                    />
                                )
                            )}
                        </div>
                    ) : loginHistory.length === 0 ? (
                        <p className="text-sm text-base-content/50">
                            Nenhum acesso registrado.
                        </p>
                    ) : (
                        <div className="flex flex-col gap-4">
                            {groupLoginsByDay(loginHistory).map(
                                group => (
                                    <div key={group.label}>
                                        <p className="text-xs font-semibold uppercase tracking-wide text-base-content/50 mb-2">
                                            {group.label}
                                        </p>
                                        <div className="flex flex-col gap-2">
                                            {group.items.map(
                                                item => (
                                                    <div
                                                        key={
                                                            item.id
                                                        }
                                                        className="flex items-center justify-between p-3 rounded-lg bg-base-100 border border-base-300/50"
                                                    >
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium text-base-content truncate">
                                                                {describeUserAgent(
                                                                    item.userAgent
                                                                )}
                                                            </p>
                                                            <p className="text-xs text-base-content/50">
                                                                {item.ipAddress ||
                                                                    'IP desconhecido'}
                                                            </p>
                                                        </div>
                                                        <span className="text-xs text-base-content/60 whitespace-nowrap ml-3">
                                                            {new Date(
                                                                item.createdAt
                                                            ).toLocaleTimeString(
                                                                'pt-BR',
                                                                {
                                                                    hour: '2-digit',
                                                                    minute: '2-digit',
                                                                }
                                                            )}
                                                        </span>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </div>
            </div>
    )
}
